import { Drawer, FormControl, List, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import BackToHomeOrgIcon from "../../assets/icons/back-button.svg?react";
import { selectCurrentUser } from "../../features/authentication/services";
import { useGetAccountInfoQuery, useGetDiningOptionsQuery, useGetStoreInfoQuery } from "../../features/sales/services";
import { useAppSelector } from "../../hooks";
import { INavItem } from "../../types/interfaces";
import classes from "./Sidebar.module.scss";

//import SidebarMenuItem from "./SidebarMenuItem"; // 有子項目的 sidebar menu item, 目前尚未使用到

interface IProps {
    drawerWidth: number;
    navItems: INavItem[];
    mobileOpen: boolean;
    handleDrawerToggle: () => void;
}

interface StoreMenuItemProps {
    storeId: string;
    storeName: string;
    isActive: boolean;
    orgId: string;
    onNavigate: () => void;
}

const StoreMenuItem = ({ storeId, storeName, isActive, orgId, onNavigate }: StoreMenuItemProps) => {
    const { t } = useTranslation();

    // Fetch dining options (including chat_mode) for this store
    const { data: diningOptionsData } = useGetDiningOptionsQuery({
        store_id: storeId,
    });

    const chatMode = diningOptionsData?.data?.chat_mode;
    const modeText =
        chatMode === "featured_recommendations"
            ? t("SALES.FeaturedRecommendations") || "展場"
            : t("SALES.OrderFlow") || "點餐";

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                onNavigate();
            }}
            className={`${classes.subNavItem} ${isActive ? classes.activeSubNavItem : ""}`}
        >
            <span className={classes.storeName}>{storeName}</span>
            <span className={classes.storeMode}>({modeText})</span>
        </div>
    );
};

const Sidebar = ({ navItems, drawerWidth, mobileOpen, handleDrawerToggle }: IProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Sidebar 掛在 PrivateLayout 層，不在 :orgId 路由 context 下，
    // useParams() 拿不到 orgId，必須直接從 pathname 解析。
    // amaze-frontend 路徑格式：/org/{orgId}/{category}
    const pathSegments = pathname.split("/");
    const routeOrgId = pathSegments[1] === "org" ? pathSegments[2] : undefined;
    const currentUser = useAppSelector(selectCurrentUser);
    const orgId = routeOrgId || currentUser?.org_id;
    const pageCategory = pathname.split("/")[3];
    const isSaleUser = Boolean(useAppSelector(selectCurrentUser)?.salesAccountId);

    const { data: accountInfo } = useGetAccountInfoQuery(isSaleUser ? undefined : skipToken);
    const { data: storesData } = useGetStoreInfoQuery(
        isSaleUser && orgId
            ? {
                  org_id: orgId,
                  page: 1,
                  limit: 100,
              }
            : skipToken
    );
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const storeName = accountInfo?.data?.display_name;
    const currentUserRoles = isSaleUser && accountInfo?.data.roles.map((role) => role.role_code);
    const storeList = storesData?.data?.result || [];
    const accessibleOrgs = accountInfo?.data?.accessible_orgs || [];
    const isDistributorAdmin = Boolean(currentUserRoles && currentUserRoles.includes("DISTRIBUTOR_ADMIN"));
    const homeOrg = accessibleOrgs.find((org) => org.source === "home_org");
    const managedOrgs = accessibleOrgs.filter((org) => org.source === "managed_org");
    const showOrgSelector = isDistributorAdmin && managedOrgs.length > 0;
    const isOnManagedOrg = Boolean(homeOrg && orgId && orgId !== homeOrg.id);
    const currentManagedOrg = managedOrgs.find((org) => org.id === orgId);
    const selectedManagedOrgId = currentManagedOrg?.id ?? "";

    const handleOrgChange = (event: SelectChangeEvent<string>) => {
        const nextOrgId = event.target.value;
        if (!nextOrgId || nextOrgId === orgId) return;
        navigate(`/org/${nextOrgId}/info`);
    };

    const handleBackToHomeOrg = () => {
        if (!homeOrg || homeOrg.id === orgId) return;
        navigate(`/org/${homeOrg.id}/info`);
    };
    const isOnDistributorHome = showOrgSelector && !isOnManagedOrg;

    const routes = [
        {
            path: `/org/${orgId}/info`,
            name: t("SALES.OrgInfo"),
        },
        {
            path: `/org/${orgId}/amaze-link`,
            name: t("SALES.AmazeLink"),
        },
        // {
        //     path: `/org/${orgId}/api-keys`,
        //     name: t("SALES.ApiKeys"),
        //     permission: ["ORG_ADMIN"],
        // },
        {
            path: `/org/${orgId}/item`,
            name: t("SALES.ItemManage"),
        },
        ...(isOnDistributorHome
            ? []
            : [
                  {
                      path: `/org/${orgId}/store`,
                      name: t("SALES.BranchManage"),
                      permission: ["ORG_ADMIN"],
                      // isDeveloping: true, // 開發完成後，再移除
                  },
              ]),
    ];

    useEffect(() => {
        // 設置視窗寬度變化的監聽器
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setWindowWidth(newWidth);
        };

        // 添加事件監聽器
        window.addEventListener("resize", handleResize);

        // 清除事件監聽器
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [handleDrawerToggle, windowWidth]);

    const drawerContent = (
        <>
            {isSaleUser ? (
                //TODO: 需要新增銷售後台的內容
                <div id='sidebar-firstChild' className={classes.drawerContent}>
                    <div className={classes.navWrapper}>
                        <div className={classes.navTitle}>{storeName}</div>
                        {showOrgSelector && (
                            <div className={classes.orgSelectorWrapper}>
                                {isOnManagedOrg && homeOrg && (
                                    <button
                                        type='button'
                                        className={classes.backToHomeBtn}
                                        onClick={handleBackToHomeOrg}
                                    >
                                        <BackToHomeOrgIcon className={classes.homeOrgLogo} />
                                        <span className={classes.backToHomeText}>{t("SALES.BackToDistributor")}</span>
                                    </button>
                                )}
                                <div className={classes.orgSelectorLabel}>{t("SALES.SwitchToSubOrg")}</div>
                                <FormControl fullWidth size='small'>
                                    <Select
                                        value={selectedManagedOrgId || ""}
                                        onChange={handleOrgChange}
                                        displayEmpty
                                        className={classes.orgSelector}
                                        renderValue={(value) => {
                                            if (!value) {
                                                return (
                                                    <span className={classes.orgSelectorPlaceholder}>
                                                        {t("SALES.SwitchToSubOrg")}
                                                    </span>
                                                );
                                            }
                                            const selected = managedOrgs.find((org) => org.id === value);
                                            return (
                                                <span className={classes.orgOptionName}>
                                                    {selected?.org_name || ""}
                                                </span>
                                            );
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: classes.orgSelectorMenu,
                                            },
                                        }}
                                    >
                                        {managedOrgs.map((org) => (
                                            <MenuItem key={org.id} value={org.id}>
                                                <span className={classes.orgOptionName}>{org.org_name}</span>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        )}
                        <List>
                            <div className={classes.navContent}>
                                {routes.map((route) => {
                                    const isActive = pageCategory === route.path.split("/")[3];
                                    const hasPermission = route.permission
                                        ? route.permission.some((permission) => {
                                              if (currentUserRoles) {
                                                  return currentUserRoles.includes(permission);
                                              }
                                              return false;
                                          })
                                        : true;
                                    if (!hasPermission) return null;
                                    // if (route.isDeveloping && isProduction) return null;

                                    const isStoreRoute = route.path.includes("/store");
                                    const currentStoreId = pathname.split("/")[4];
                                    const hasSelectedStore =
                                        isStoreRoute && pageCategory === "store" && Boolean(currentStoreId);
                                    const showActive = isActive && !hasSelectedStore;
                                    const showStoreSubList = isStoreRoute && (!showOrgSelector || isOnManagedOrg);

                                    return (
                                        <div key={route.path}>
                                            <div
                                                onClick={() => {
                                                    navigate(route.path);
                                                }}
                                                className={`${classes.navItem} ${
                                                    showActive ? classes.activeNavItem : ""
                                                }`}
                                            >
                                                {route.name}
                                            </div>
                                            {showStoreSubList && storeList.length > 0 && (
                                                <div className={classes.subMenu}>
                                                    {storeList.map((store) => {
                                                        const isStoreActive = currentStoreId === store.id;
                                                        return (
                                                            <StoreMenuItem
                                                                key={store.id}
                                                                storeId={store.id}
                                                                storeName={store.store_name}
                                                                isActive={isStoreActive}
                                                                orgId={orgId || ""}
                                                                onNavigate={() =>
                                                                    navigate(
                                                                        `/org/${orgId}/store/${store.id}/card-settings`
                                                                    )
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </List>
                    </div>
                </div>
            ) : null}
        </>
    );
    return (
        <>
            {windowWidth < 900 && (
                <Drawer
                    variant='temporary'
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: false,
                    }}
                    sx={{
                        display: { xs: "block", md: "block" },
                        //* 把常駐專案列表藏起來 */
                        // marginTop: { xs: "56px", md: "0rem" },
                        marginTop: { xs: "56px", md: "6rem" },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
            {/* 把常駐專案列表藏起來 */}
            {mobileOpen && (
                <Drawer
                    variant='permanent'
                    sx={{
                        width: drawerWidth,
                        display: { xs: "none", md: "block" },
                        marginTop: { xs: "0", md: "6rem" },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default Sidebar;
